/**
 * MODULE: _shared/frontend-shell/src/services/docxUploadService
 * PURPOSE: Upload Word documents, extract styles/content; DOCX→template conversion
 * DEPENDENCIES: API_CONFIG, getAuthHeaders; @domain/sales-presentations (DocumentStyleSchema, DocxUploadMetadata)
 * PUBLIC API: DocxUploadService (uploadDocx, extractStyle, convertToTemplate, generateAIStyle, extractContent)
 * SIDE EFFECTS: HTTP to /api/v1/presentations/templates (upload-docx, extract-document-style, etc.)
 * AGENT SAFETY: 🟢 SAFE
 * Recent: 2026-02-19 – Document template builder; style extraction; DOCX upload (25MB limit)
 */

import { API_CONFIG, getAuthHeaders } from '../../../lib/frontend/api/config';
import type {
  DocumentStyleSchema,
  DocxUploadMetadata,
} from '@domain/sales-presentations/api/types';

const API_BASE = API_CONFIG.baseURL
  ? `${API_CONFIG.baseURL}/api/v1/presentations/templates`
  : '/api/v1/presentations/templates';

export interface DocxUploadResult {
  success: boolean;
  file_url?: string;
  metadata?: DocxUploadMetadata;
  error?: string;
}

export interface StyleExtractionResult {
  success: boolean;
  style?: DocumentStyleSchema;
  page_count?: number;
  word_count?: number;
  error?: string;
}

export class DocxUploadService {
  static async uploadDocx(file: File): Promise<DocxUploadResult> {
    try {
      if (!file.name.toLowerCase().endsWith('.docx')) {
        return { success: false, error: 'Please upload a valid Word document (.docx)' };
      }
      if (file.size > 25 * 1024 * 1024) {
        return { success: false, error: 'File size must be less than 25MB' };
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/upload-docx`, {
        method: 'POST',
        headers: getAuthHeaders() as HeadersInit,
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Upload failed');
      }

      const data = await response.json();
      return {
        success: true,
        file_url: data.file_url,
        metadata: data.metadata,
      };
    } catch (err) {
      console.error('DOCX upload error:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to upload file. Please try again.',
      };
    }
  }

  static async extractStyle(fileUrl: string): Promise<StyleExtractionResult> {
    try {
      const response = await fetch(`${API_BASE}/extract-document-style`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthHeaders() as Record<string, string>),
        },
        body: JSON.stringify({ file_url: fileUrl }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Style extraction failed');
      }

      const data = await response.json();
      return {
        success: true,
        style: data.style,
        page_count: data.page_count,
        word_count: data.word_count,
      };
    } catch (err) {
      console.error('Style extraction error:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to extract style. Please try again.',
      };
    }
  }

  static async generateAIStyle(
    fileUrl: string,
    styleName: string
  ): Promise<StyleExtractionResult> {
    try {
      const response = await fetch(`${API_BASE}/generate-ai-document-style`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthHeaders() as Record<string, string>),
        },
        body: JSON.stringify({ file_url: fileUrl, style_name: styleName }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'AI style generation failed');
      }

      const data = await response.json();
      return {
        success: true,
        style: data.style,
        page_count: data.page_count,
        word_count: data.word_count,
      };
    } catch (err) {
      console.error('AI style generation error:', err);
      return {
        success: false,
        error:
          err instanceof Error ? err.message : 'Failed to generate AI style. Please try again.',
      };
    }
  }

  static async extractContent(fileUrl: string): Promise<{
    success: boolean;
    content?: string;
    sections?: unknown[];
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE}/extract-docx-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthHeaders() as Record<string, string>),
        },
        body: JSON.stringify({ file_url: fileUrl }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Content extraction failed');
      }

      const data = await response.json();
      return {
        success: true,
        content: data.content,
        sections: data.sections,
      };
    } catch (err) {
      console.error('Content extraction error:', err);
      return {
        success: false,
        error: 'Failed to extract content',
      };
    }
  }
}
